import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';
import Footer from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user: currentUser, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [processingSession, setProcessingSession] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    preferred_currency: 'USD',
    role: 'school_student',
    grade: '9',
    education: '',
    area_of_interest: '',
    working_area: ''
  });

  useEffect(() => {
    // Check for token in URL parameter (from Google OAuth callback)
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    
    if (tokenFromUrl) {
      // Store token and fetch user data
      localStorage.setItem('token', tokenFromUrl);
      setProcessingSession(true);
      fetchUserData(tokenFromUrl);
    } else if (currentUser && currentUser.profile_completed) {
      // If user already has profile completed, redirect to dashboard
      navigate('/dashboard');
    }
  }, [location, currentUser, navigate]);

  const fetchUserData = async (tokenToUse) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`
        }
      });

      const userData = response.data;
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update AuthContext
      login(tokenToUse, userData);

      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);

      if (userData.profile_completed) {
        // Profile already completed, redirect to dashboard
        navigate('/dashboard');
      } else {
        // Need to complete profile - show form
        setProcessingSession(false);
        toast.success(`Welcome, ${userData.name}! Please complete your profile to continue.`);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      toast.error('Failed to load user data. Please try logging in again.');
      setProcessingSession(false);
      navigate('/login');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Add conditional fields based on role
      if (formData.role === 'school_student') {
        submitData.grade = parseInt(formData.grade);
      }

      const response = await axios.post(
        `${API}/auth/complete-profile`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('Profile completed successfully!');
      
      // Reload page to update user context
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Profile completion error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-3xl">
        {processingSession ? (
          <Card className="p-10 shadow-xl border-2 border-blue-100 bg-white text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <h2 className="text-xl font-semibold text-gray-700">Processing your login...</h2>
              <p className="text-gray-500">Please wait while we set up your account</p>
            </div>
          </Card>
        ) : (
          <Card className="p-6 md:p-10 shadow-xl border-2 border-blue-100 bg-white">
            <div className="text-center mb-8">
              <div className="mb-4">
                {currentUser?.picture && (
                  <img
                    src={currentUser.picture}
                    alt="Profile"
                    className="w-20 h-20 rounded-full mx-auto border-4 border-blue-300"
                  />
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-blue-600 mb-3">
                Welcome, {currentUser?.name}!
              </h1>
              <p className="text-base md:text-lg text-gray-600">
                Complete your profile to get started
              </p>
            </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+91-1234567890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Gender</Label>
                  <Select 
                    value={formData.gender} 
                    onValueChange={(val) => setFormData({ ...formData, gender: val })}
                    required
                  >
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select your gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">
                Address Details
              </h3>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">Address</Label>
                <Input
                  type="text"
                  placeholder="Street address, building, apartment"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">City</Label>
                  <Input
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">State</Label>
                  <Input
                    type="text"
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Country</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(val) => {
                      const currencyMap = {
                        'India': 'INR',
                        'United States': 'USD',
                        'United Kingdom': 'GBP',
                        'Canada': 'CAD',
                        'Australia': 'AUD',
                        'Germany': 'EUR',
                        'France': 'EUR',
                        'Japan': 'JPY',
                        'China': 'CNY'
                      };
                      setFormData(prev => ({
                        ...prev,
                        country: val,
                        preferred_currency: currencyMap[val] || 'USD'
                      }));
                    }}
                  >
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Japan">Japan</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Postal Code</Label>
                  <Input
                    type="text"
                    placeholder="ZIP/Postal code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Preferred Currency</Label>
                <Select 
                  value={formData.preferred_currency} 
                  onValueChange={(val) => setFormData({ ...formData, preferred_currency: val })}
                >
                  <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹) - Indian Rupee</SelectItem>
                    <SelectItem value="USD">USD ($) - US Dollar</SelectItem>
                    <SelectItem value="GBP">GBP (£) - British Pound</SelectItem>
                    <SelectItem value="EUR">EUR (€) - Euro</SelectItem>
                    <SelectItem value="CAD">CAD ($) - Canadian Dollar</SelectItem>
                    <SelectItem value="AUD">AUD ($) - Australian Dollar</SelectItem>
                    <SelectItem value="JPY">JPY (¥) - Japanese Yen</SelectItem>
                    <SelectItem value="CNY">CNY (¥) - Chinese Yuan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Professional Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">
                Professional Details
              </h3>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">I am a</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                  required
                >
                  <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school_student">School Student</SelectItem>
                    <SelectItem value="college_student">College Student</SelectItem>
                    <SelectItem value="jobseeker">Jobseeker</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional fields based on role */}
              {formData.role === 'school_student' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Grade/Class</Label>
                  <Select 
                    value={formData.grade} 
                    onValueChange={(val) => setFormData({ ...formData, grade: val })}
                    required
                  >
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['9', '10', '11', '12'].map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          Grade {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.role === 'college_student' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Education/Program</Label>
                  <Select 
                    value={formData.education} 
                    onValueChange={(val) => setFormData({ ...formData, education: val })}
                    required
                  >
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select your program" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BSc">BSc</SelectItem>
                      <SelectItem value="BA">BA</SelectItem>
                      <SelectItem value="BCom">BCom</SelectItem>
                      <SelectItem value="BCA">BCA</SelectItem>
                      <SelectItem value="MCA">MCA</SelectItem>
                      <SelectItem value="BBA">BBA</SelectItem>
                      <SelectItem value="MBA">MBA</SelectItem>
                      <SelectItem value="Engineering">Engineering</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.role === 'jobseeker' && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Education</Label>
                    <Input
                      type="text"
                      placeholder="Your highest education"
                      value={formData.education}
                      onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                      required
                      className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Area of Interest</Label>
                    <Input
                      type="text"
                      placeholder="e.g., Marketing, IT, Finance"
                      value={formData.area_of_interest}
                      onChange={(e) => setFormData({ ...formData, area_of_interest: e.target.value })}
                      required
                      className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              {formData.role === 'professional' && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Education</Label>
                    <Input
                      type="text"
                      placeholder="Your highest education"
                      value={formData.education}
                      onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                      required
                      className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Working Area</Label>
                    <Input
                      type="text"
                      placeholder="e.g., Software Development, Teaching"
                      value={formData.working_area}
                      onChange={(e) => setFormData({ ...formData, working_area: e.target.value })}
                      required
                      className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all"
              disabled={loading}
            >
              {loading ? 'Completing Profile...' : 'Complete Profile & Continue'}
            </Button>
          </form>
        </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProfileCompletion;
